import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Users, Megaphone, BarChart3, Sparkles, ArrowRight,
  Activity, MessageSquare, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getOutreachStatus, getOutreachLogs } from '../../lib/outreach-api';

const QUICK_LINKS = [
  { to: '/outreach/leads', icon: Users, label: 'Leads / CRM',
    desc: 'Manage outreach contacts and lead status' },
  { to: '/outreach/campaigns', icon: Megaphone, label: 'Campaigns',
    desc: 'Engine, queue and custom outreach commands' },
  { to: '/outreach/analytics', icon: BarChart3, label: 'Analytics',
    desc: 'Replies, conversions and pacing' },
  { to: '/outreach/settings', icon: Sparkles, label: 'AI Settings',
    desc: 'Outreach AI personality and behavior' },
];

export const OutreachDashboardPage = () => {
  const [status, setStatus] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          getOutreachStatus(),
          getOutreachLogs(8),
        ]);
        setStatus(s.data);
        setRecentLogs(Array.isArray(l.data) ? l.data : []);
      } catch (e) {
        // best-effort; UI still renders
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const engineRunning = status?.engine_running;
  const today = status?.today || {};
  const total = status?.total || {};

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="outreach-dashboard">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
              Outreach AI
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Outreach Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            A separate AI agent for relationship-building, first-touch and lead nurturing.
            Independent from the Support AI.
          </p>
        </div>
        <Badge
          variant="outline"
          className={engineRunning
            ? 'border-emerald-500/40 text-emerald-400'
            : 'border-amber-500/40 text-amber-400'}
          data-testid="engine-status-badge"
        >
          {loading ? '…' : engineRunning ? 'Engine running' : 'Engine paused'}
        </Badge>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-strip">
        {[
          { label: 'Sent today', value: today.sent ?? '–', icon: MessageSquare },
          { label: 'Replies today', value: today.replies ?? '–', icon: Activity },
          { label: 'Total sent', value: total.sent ?? '–', icon: MessageSquare },
          { label: 'Total replies', value: total.replies ?? '–', icon: Activity },
        ].map((k) => (
          <Card key={k.label} className="bg-card/40 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-2xl font-semibold tracking-tight">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : k.value}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick links */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Quick navigation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK_LINKS.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`quick-link-${q.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
            >
              <Card className="h-full transition hover:border-amber-500/40 hover:bg-amber-500/[0.04]">
                <CardContent className="p-5">
                  <q.icon className="w-5 h-5 text-amber-400 mb-3" />
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{q.label}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{q.desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent outreach activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
          {!loading && recentLogs.length === 0 && (
            <p className="text-sm text-muted-foreground">No outreach activity yet.</p>
          )}
          {!loading && recentLogs.map((log) => (
            <div
              key={log.id}
              className="flex items-start justify-between gap-3 p-3 rounded-md border border-border/50 bg-muted/20"
              data-testid={`log-${log.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {log.intent}
                  </Badge>
                  <span className="text-sm font-medium truncate">
                    {log.lead_name || log.lead_phone}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{log.message}</p>
              </div>
              <Badge
                className="shrink-0"
                variant={log.status === 'replied' ? 'default' : 'outline'}
              >
                {log.status}
              </Badge>
            </div>
          ))}
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-amber-400 hover:text-amber-300"
            data-testid="view-all-logs-btn"
          >
            <Link to="/outreach/logs">View all activity →</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OutreachDashboardPage;
